import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../components/header";
import API_BASE_URL from "../../utils/apiConfig";
import { FaArrowLeft, FaSave, FaCheck, FaImages, FaTimes, FaUpload } from "react-icons/fa";

interface ContentItem {
  id: number;
  key: string;
  value: string;
  type: string;
  group: string;
  label: string;
}

interface MediaItem {
  id: number;
  filename: string;
  original_name: string;
  url: string;
}

export default function CmsSectionEditor() {
  const { group } = useParams<{ group: string }>();
  const navigate = useNavigate();
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [mediaPickerFor, setMediaPickerFor] = useState<string | null>(null);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData || JSON.parse(userData).role !== "admin") {
      navigate("/home");
      return;
    }
    fetchContent();
  }, [group, navigate]);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/cms/admin/content`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data[group || ""] || []);
      }
    } catch (err) {
      setError("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    setMediaLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/cms/admin/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setMedia(await res.json());
    } catch (err) {
      console.error("Failed to load media");
    } finally {
      setMediaLoading(false);
    }
  };

  const handleUploadInPicker = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE_URL}/api/cms/admin/media`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) await fetchMedia();
    } catch (err) {
      console.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openMediaPicker = (key: string) => {
    setMediaPickerFor(key);
    if (media.length === 0) fetchMedia();
  };

  const selectMedia = (url: string) => {
    if (mediaPickerFor) {
      handleChange(mediaPickerFor, url);
      setMediaPickerFor(null);
    }
  };

  const handleChange = (key: string, value: string) => {
    setItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, value } : item))
    );
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/cms/admin/content`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: items.map((item) => ({ key: item.key, value: item.value })),
        }),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError("Failed to save. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const groupTitle = (group || "").charAt(0).toUpperCase() + (group || "").slice(1);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-[#0a2e30] to-[#061e1f] pt-24 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <button onClick={() => navigate("/cms")} className="cursor-pointer bg-white/10 hover:bg-white/20 text-white rounded-lg p-3 transition-colors">
              <FaArrowLeft />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Edit: {groupTitle}</h1>
              <p className="text-gray-400 text-sm">Modify content below and click Save</p>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-300 rounded-lg p-4 mb-6">{error}</div>
          )}

          {loading ? (
            <div className="text-white text-center py-12">Loading content...</div>
          ) : (
            <div className="space-y-6">
              {items.map((item) => (
                <div key={item.key} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                  <label className="block text-white font-medium mb-2">{item.label}</label>
                  <p className="text-gray-500 text-xs mb-3 font-mono">{item.key}</p>

                  {item.type === "image" ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="text"
                          value={item.value || ""}
                          onChange={(e) => handleChange(item.key, e.target.value)}
                          placeholder="Image URL (e.g. /storage/uploads/image.png)"
                          className="flex-1 bg-white/10 text-white border border-white/20 rounded-lg p-4 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 transition-colors text-sm"
                        />
                        <button
                          onClick={() => openMediaPicker(item.key)}
                          className="cursor-pointer bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-400 px-4 py-4 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <FaImages /> Pick
                        </button>
                      </div>
                      {item.value && (
                        <div className="bg-black/30 rounded-lg p-3 inline-block">
                          <img
                            src={item.value.startsWith("http") ? item.value : `${API_BASE_URL}${item.value}`}
                            alt="Preview"
                            className="max-w-[200px] max-h-[120px] object-contain rounded"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                          />
                        </div>
                      )}
                    </div>
                  ) : item.type === "textarea" || item.type === "html" ? (
                    <textarea
                      value={item.value || ""}
                      onChange={(e) => handleChange(item.key, e.target.value)}
                      rows={4}
                      className="w-full bg-white/10 text-white border border-white/20 rounded-lg p-4 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 transition-colors resize-y"
                    />
                  ) : (
                    <input
                      type="text"
                      value={item.value || ""}
                      onChange={(e) => handleChange(item.key, e.target.value)}
                      className="w-full bg-white/10 text-white border border-white/20 rounded-lg p-4 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 transition-colors"
                    />
                  )}
                </div>
              ))}

              <div className="sticky bottom-6 flex justify-end pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`cursor-pointer flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 ${
                    saved ? "bg-emerald-600" : "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 hover:shadow-emerald-500/25"
                  } disabled:opacity-50`}
                >
                  {saved ? <><FaCheck /> Saved!</> : saving ? "Saving..." : <><FaSave /> Save Changes</>}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Media Picker Modal */}
        {mediaPickerFor && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setMediaPickerFor(null)}>
            <div className="bg-[#0d3537] rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden shadow-2xl border border-white/10" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h3 className="text-xl font-bold text-white">Select Image</h3>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                    <FaUpload /> {uploading ? "Uploading..." : "Upload New"}
                    <input type="file" accept="image/*" onChange={handleUploadInPicker} className="hidden" disabled={uploading} />
                  </label>
                  <button onClick={() => setMediaPickerFor(null)} className="cursor-pointer text-gray-400 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors">
                    <FaTimes className="text-xl" />
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {mediaLoading ? (
                  <p className="text-white text-center py-8">Loading media...</p>
                ) : media.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No media uploaded. Click Upload New to add images.</p>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {media.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => selectMedia(m.url)}
                        className="cursor-pointer group relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-emerald-400 transition-all"
                      >
                        <img
                          src={`${API_BASE_URL}${m.url}`}
                          alt={m.original_name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                          <span className="text-white text-xs truncate">{m.original_name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
